// src/Auth/AuthMicrosoft/authMicrosoft.js
import { loginWithMicrosoft } from "../../lib/api"
import { API_BASE } from "../../lib/apiHelper"
import { devError, devLog, devWarn } from "../../utils/devConsole"

// Variabile per tracciare lo stato di disconnessione
let isLoggingOut = false

// Funzione per verificare e pulire le interazioni in corso
export const clearPendingInteractions = async (instance) => {
  try {

    // Tenta di recuperare e processare qualsiasi interazione in attesa
    await instance.handleRedirectPromise().catch((e) => devWarn("Error handleRedirectPromise:", e))

    // Verifica se ci sono interazioni in corso nel sessionStorage
    const storageKeys = Object.keys(sessionStorage)
    const interactionKeys = storageKeys.filter(
      (key) => key.startsWith("msal.") || key.includes("interaction") || key.includes("authority"),
    )

    // Rimuove tutte le voci di interazione dal sessionStorage
    if (interactionKeys.length > 0) {
      interactionKeys.forEach((key) => sessionStorage.removeItem(key))
    }

    // Verifica anche localStorage - SOLO le chiavi Microsoft, non i token di auth
    const localStorageKeys = Object.keys(localStorage);
    const msalLocalKeys = localStorageKeys.filter(
      (key) => key.startsWith("msal.") || key.includes("interaction") || key.includes("authority"),
    )

    if (msalLocalKeys.length > 0) {
      msalLocalKeys.forEach((key) => localStorage.removeItem(key))
    }
    
    // NON rimuovere authToken, authMethod, connectionTime - appartengono all'app

  } catch (error) {
    devError("Error during interaction cleanup:", error);
    // Continua nonostante l'errore
  }
}

// Funzione di login tramite Microsoft (reindirizzamento)
export const loginMicrosoft = async (instance) => {
  try {
    // Verifica se è in corso una disconnessione
    if (isLoggingOut) {
      devWarn("A disconnection is in progress, cannot connect now")
      throw new Error("Disconnection in progress, try again in a few moments")
    }

    // Pulisce prima qualsiasi interazione in corso
    await clearPendingInteractions(instance)

    // Attende un breve istante per assicurarsi che la pulizia sia terminata
    await new Promise((resolve) => setTimeout(resolve, 500))

    // Poi avvia la nuova autenticazione
    await instance.loginRedirect({
      scopes: ["User.Read"],
      prompt: "select_account",
    })
  } catch (error) {
    devError("Microsoft login error:", error)
    throw error
  }
}

// Funzione di gestione della risposta dopo il reindirizzamento
export const handleMicrosoftResponse = async (instance, navigate) => {
  try {
    // Verifica se è in corso una disconnessione
    if (isLoggingOut) {
      devWarn("A disconnection is in progress, cannot process response now")
      return
    }

    const response = await instance.handleRedirectPromise()

    if (!response) {
      devWarn("No authentication response found. Checking cached account...")
      const accounts = instance.getAllAccounts()
      if (accounts.length > 0) {
        instance.setActiveAccount(accounts[0])
        navigate("/service")
      }
      return
    }

    if (response.accessToken) {
      const userData = await loginWithMicrosoft(response.accessToken)

      if (userData.token) {
        // Connessione autorizzata - Microsoft gestisce già il controllo del tenant
        instance.setActiveAccount(instance.getAllAccounts()[0])
        localStorage.setItem("authToken", userData.token)
        localStorage.setItem("authMethod", "microsoft")
        navigate("/service")
        return userData
      }
    }
  } catch (error) {
    devError("Error in Microsoft authentication response:", error)
    // In caso di errore, pulisci le interazioni in corso
    await clearPendingInteractions(instance)
  }
}

// Funzione di disconnessione migliorata
export const logoutMicrosoft = async (instance) => {
  try {
    // Verifica se è già in corso una disconnessione
    if (isLoggingOut) {
      devWarn("⚠️ [LOGOUT] Disconnection already in progress, waiting...");
      return;
    }

    devLog("🔄 [LOGOUT] Microsoft disconnection in progress...");

    // Marcare che la disconnessione è in corso
    isLoggingOut = true;

    // 1. Prima, pulire qualsiasi interazione in corso
    await clearPendingInteractions(instance);

    // 2. Chiamare l'API di disconnessione del backend con timeout
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 secondi timeout

      await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      devLog("✅ [LOGOUT] Backend logout successful");
    } catch (backendError) {
      devWarn("⚠️ [LOGOUT] Backend logout failed, continuing with frontend logout:", backendError);
    }

    // 3. Pulire lo storage locale - SOLO se è una disconnessione Microsoft
    const authMethod = localStorage.getItem("authMethod");
    if (authMethod === "microsoft") {
      localStorage.removeItem("authToken");
      localStorage.removeItem("authMethod");
    }

    // 4. Pulire solo le chiavi MSAL dal sessionStorage
    const msalKeys = Object.keys(sessionStorage).filter(key => key.startsWith("msal."));
    msalKeys.forEach(key => sessionStorage.removeItem(key));

    // 5. Pulire la cache MSAL
    instance.clearCache();

    // 6. Disconnessione Microsoft COMPLETA (anche lato server)
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      
      // Disconnessione con reindirizzamento verso Microsoft per disconnettere lato server
      const logoutRequest = {
        account: accounts[0],
        postLogoutRedirectUri: window.location.origin, // Reindirizzamento verso la radice del sito
      };
      
      await instance.logoutRedirect(logoutRequest);
      return; // Fermare l'esecuzione perché saremo reindirizzati
    } else {
    }

    // 9. Reimpostare lo stato di disconnessione dopo un ritardo
    setTimeout(() => {
      isLoggingOut = false;
    }, 2000); // Ridotto da 3 a 2 secondi
  } catch (error) {
    devError("Error during logout:", error)

    // In caso di errore, forzare una disconnessione "brutale"
    try {
      // Pulire manualmente
      localStorage.clear()
      sessionStorage.clear()

      // Reindirizzare verso la pagina principale
      window.location.href = "/"
    } catch (finalError) {
      devError("Fatal error during logout cleanup:", finalError)
    } finally {
      // Reimpostare lo stato di disconnessione
      setTimeout(() => {
        isLoggingOut = false;
      }, 2000); // Ridotto da 3 a 2 secondi
    }
  }
}
